import React from 'react';
import {
  Badge,
  Button,
  type ButtonProps,
  Flex,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { getQuickStatusFilterButtonAppearance } from './QuickStatusFilterButtonStyles';

interface QuickStatusFilterButtonProps
  extends Omit<ButtonProps, 'onClick' | 'children'> {
  label: React.ReactNode;
  count: number;
  active: boolean;
  onClick: () => void;
  dataTestId?: string;
  contentDirection?: 'row' | 'column';
  contentGap?: number | string;
  muted?: boolean;
  compact?: boolean;
}

export const QuickStatusFilterButton = React.forwardRef<
  HTMLButtonElement,
  QuickStatusFilterButtonProps
>(
  (
    {
      label,
      count,
      active,
      onClick,
      dataTestId,
      contentDirection = 'column',
      contentGap = 1,
      muted = false,
      compact = false,
      ...buttonProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const computedColorScheme = useComputedColorScheme('dark');
    const appearance = getQuickStatusFilterButtonAppearance(
      theme,
      computedColorScheme,
      active,
      muted,
      compact
    );

    return (
      <Button
        ref={ref}
        variant="transparent"
        c={appearance.color}
        onClick={onClick}
        data-testid={dataTestId}
        aria-pressed={active}
        radius={appearance.radius}
        style={appearance.buttonStyle}
        px="sm"
        py={6}
        styles={appearance.styles}
        {...buttonProps}
      >
        <Flex
          direction={contentDirection}
          align="center"
          w="100%"
          gap={contentGap}
        >
          <Text
            size="sm"
            c={appearance.textColor}
            fw={appearance.fontWeight}
            style={appearance.textStyle}
          >
            {label}
          </Text>
          <Badge
            size="xs"
            radius="xl"
            variant={appearance.badgeVariant}
            color={appearance.color}
            style={appearance.countStyle}
          >
            {count}
          </Badge>
        </Flex>
      </Button>
    );
  }
);

QuickStatusFilterButton.displayName = 'QuickStatusFilterButton';
